import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { analyticsAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import { toast } from 'sonner';
import { ChartCard, CustomTooltip, KpiCard, colorDeSerie } from '@/components/charts';
import {
  Ticket, MessageSquare, Bot, AlertTriangle, TrendingUp,
  Activity, Clock, ArrowUpRight, ArrowDownRight, Zap, Radio, HeartPulse, Coins, DollarSign, Trash2, ExternalLink
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, Legend
} from 'recharts';
import { fechaYHora } from '../lib/fechas';

const COLORS = {
  primary: '#002FA7',
  primaryLight: '#3366CC',
  success: '#00D15C',
  warning: '#FFB800',
  danger: '#FF2A00',
  muted: '#A1A1AA',
  bg: '#F4F4F5',
};

const PIE_COLORS = ['#002FA7', '#00D15C', '#FFB800', '#FF2A00', '#7C3AED', '#0EA5E9', '#F97316', '#EC4899'];

const PRIORITY_COLORS = {
  critical: '#FF2A00',
  high: '#F97316',
  medium: '#FFB800',
  low: '#00D15C',
};

const STATUS_COLORS = {
  new: '#002FA7',
  open: '#0EA5E9',
  in_progress: '#FFB800',
  waiting_user: '#7C3AED',
  resolved: '#00D15C',
  closed: '#A1A1AA',
};




function PieLabel({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" className="text-[10px] font-bold">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

const TOKEN_PURGE_PHRASE = 'ELIMINAR CONSUMOS';

function formatTokens(n) {
  if (n == null || Number.isNaN(Number(n))) return '-';
  return `${Number(n).toLocaleString('es-AR')} tok`;
}

function formatUsd(n) {
  if (n == null || Number.isNaN(Number(n))) return '-';
  return `USD ${Number(n).toFixed(4)}`;
}

//: Un numero largo sin unidad no se lee de un vistazo. `-` cuando no hay dato: un cero seria una
//: afirmacion.
function miles(n) {
  if (n == null || Number.isNaN(Number(n))) return '-';
  return Number(n).toLocaleString('es-AR');
}

/**
 * El detalle de costo de una porcion del grafico de propositos.
 *
 * **Por que un tooltip y no otra tabla.** El grafico ya contesta *donde* se van los tokens; lo que
 * faltaba es *cuanto cuesta cada lugar*, y eso son dos numeros mas sobre la misma porcion. Una
 * tercera tabla con la misma informacion seria una segunda declaracion del mismo hecho.
 */
function TooltipDeConsumo({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};
  return (
    <div className="bg-white border border-brand-border rounded-sm px-2.5 py-2 shadow-sm text-[11px]" data-testid="tooltip-consumo">
      <p className="font-semibold text-[#09090B] capitalize mb-1">{d.purpose}</p>
      <p className="text-zinc-600">{miles(d.total_tokens)} tokens · {formatUsd(d.cost_usd)}</p>
      <p className="text-zinc-500">
        in {miles(d.input_tokens)}
        {d.cached_input_tokens > 0 ? ` (cached ${miles(d.cached_input_tokens)})` : ''}
        {' · '}out {miles(d.output_tokens)}
      </p>
      {/* El razonamiento se paga como salida: es el numero que decide si al agente le sobra un
          modelo que razona. Si es cero no se dibuja — un cero ahi no dice nada. */}
      {d.reasoning_tokens > 0 && (
        <p className="text-brand-danger">razonamiento {miles(d.reasoning_tokens)}</p>
      )}
      <p className="text-zinc-400">{d.calls} llamadas</p>
    </div>
  );
}

function windowSub(w) {
  if (!w) return '';
  const parts = [];
  if (w.cost_usd != null || w.cost != null) parts.push(`gasto ${formatUsd(w.cost_usd ?? w.cost)}`);
  if (w.usd_per_1m_tokens != null) parts.push(`tasa ${Number(w.usd_per_1m_tokens).toFixed(2)} USD/1M`);
  if (w.calls != null) parts.push(`${w.calls} llamadas`);
  return parts.join(' · ');
}

function conversationAutopsyHref(conversationId) {
  return `/logs?tab=autopsy&conversation=${encodeURIComponent(conversationId)}`;
}

function ticketIdFromConversationId(conversationId) {
  const id = String(conversationId || '');
  if (!id.startsWith('ticket-chat-')) return null;
  return id.slice('ticket-chat-'.length).trim() || null;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [byPriority, setByPriority] = useState([]);
  const [byStatus, setByStatus] = useState([]);
  const [byChannel, setByChannel] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [agents, setAgents] = useState([]);
  const [activity, setActivity] = useState({ recent_tickets: [], recent_events: [] });
  const [tokenSummary, setTokenSummary] = useState(null);
  const [tokensByModel, setTokensByModel] = useState([]);
  const [tokensByPurpose, setTokensByPurpose] = useState([]);
  const [tokensByAgent, setTokensByAgent] = useState([]);
  const [tokensByConvo, setTokensByConvo] = useState([]);
  const [tokensTimeline, setTokensTimeline] = useState([]);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgePhrase, setPurgePhrase] = useState('');
  const [pricingRows, setPricingRows] = useState([]);
  const [pricingDraft, setPricingDraft] = useState({});
  const [pricingSaving, setPricingSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [sumRes, priRes, statusRes, chanRes, tlRes, agRes, actRes, tSumRes, tModelRes, tPurpRes, tAgentRes, tConvoRes, tTlRes] = await Promise.all([
        analyticsAPI.summary(),
        analyticsAPI.ticketsByPriority(),
        analyticsAPI.ticketsByStatus(),
        analyticsAPI.conversationsByChannel(),
        analyticsAPI.ticketsTimeline(),
        analyticsAPI.agentsPerformance(),
        analyticsAPI.recentActivity(),
        analyticsAPI.tokensSummary(),
        analyticsAPI.tokensByModel(),
        analyticsAPI.tokensByPurpose(),
        analyticsAPI.tokensByAgent(),
        analyticsAPI.tokensByConversation(),
        analyticsAPI.tokensTimeline(),
      ]);
      setSummary(sumRes.data);
      setByPriority(priRes.data);
      setByStatus(statusRes.data);
      setByChannel(chanRes.data);
      setTimeline(tlRes.data);
      setAgents(agRes.data);
      setActivity(actRes.data);
      setTokenSummary(tSumRes.data);
      setTokensByModel(tModelRes.data);
      setTokensByPurpose(tPurpRes.data);
      setTokensByAgent(tAgentRes.data);
      setTokensByConvo(tConvoRes.data);
      setTokensTimeline(tTlRes.data);

      if (isAdmin) {
        try {
          const priceRes = await analyticsAPI.tokensPricing();
          const rows = priceRes.data?.prices || [];
          setPricingRows(rows);
          const draft = {};
          rows.forEach((r) => {
            draft[r.model] = {
              input_usd_per_1m: String(r.input_usd_per_1m ?? ''),
              cached_usd_per_1m: String(r.cached_usd_per_1m ?? ''),
              output_usd_per_1m: String(r.output_usd_per_1m ?? ''),
            };
          });
          setPricingDraft(draft);
        } catch (err) {
          console.error('Token pricing load error:', err);
        }
      }
    } catch (err) {
      console.error('Analytics load error:', err);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const savePricing = async () => {
    setPricingSaving(true);
    try {
      const prices = {};
      Object.entries(pricingDraft).forEach(([model, vals]) => {
        const pin = parseFloat(vals.input_usd_per_1m);
        const pout = parseFloat(vals.output_usd_per_1m);
        const pcached = parseFloat(vals.cached_usd_per_1m);
        if (Number.isFinite(pin) && Number.isFinite(pout)) {
          const entry = { input_usd_per_1m: pin, output_usd_per_1m: pout };
          if (Number.isFinite(pcached)) entry.cached_usd_per_1m = pcached;
          prices[model] = entry;
        }
      });
      await analyticsAPI.tokensPricingPut(prices);
      toast.success('Tarifario estimado guardado');
      await loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPricingSaving(false);
    }
  };

  const s = summary || { tickets: {}, conversations: {}, agents: {}, heartbeat: {} };
  const w24 = tokenSummary?.last_24h;
  const w7 = tokenSummary?.last_7d;
  const w30 = tokenSummary?.last_30d;
  const allTime = tokenSummary?.all_time;

  return (
    <div className="p-6 max-w-[1440px] mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#09090B] tracking-tight">
            Dashboard
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Vista operativa de <span className="font-semibold text-zinc-700">{user?.tenant_id}</span>
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs font-semibold text-brand-primary hover:text-brand-primary/80 transition-colors disabled:opacity-50"
          data-testid="refresh-dashboard"
        >
          <Activity className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </button>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-6" data-testid="kpi-grid">
        <KpiCard label="Tickets Abiertos" value={s.tickets.open ?? '-'} icon={Ticket} color="text-brand-primary" sub={`${s.tickets.total ?? 0} total`} loading={loading} />
        <KpiCard label="Criticos / Alta" value={s.tickets.critical ?? '-'} icon={AlertTriangle} color="text-brand-danger" sub="Requieren atencion" loading={loading} />
        <KpiCard label="Tasa Resolucion" value={s.tickets.resolution_rate != null ? `${s.tickets.resolution_rate}%` : '-'} icon={TrendingUp} color="text-brand-success" sub={`${s.tickets.resolved ?? 0} resueltos`} loading={loading} />
        <KpiCard label="Conv. Activas" value={s.conversations.active ?? '-'} icon={MessageSquare} color="text-brand-primary" sub={`${s.conversations.waiting_user ?? 0} esperando`} loading={loading} />
        <KpiCard label="Agentes Activos" value={s.agents.active ?? '-'} icon={Bot} color="text-[#09090B]" sub={`HITL: ${s.conversations.hitl_active ?? 0}`} loading={loading} />
        <KpiCard label="Heartbeat" value={s.heartbeat.with_reminders ?? '-'} icon={HeartPulse} color="text-brand-warning" sub={`${s.heartbeat.escalated ?? 0} escaladas`} loading={loading} />
      </div>

      {/* Charts Section */}
      <Tabs defaultValue="overview" className="mb-6" data-testid="dashboard-tabs">
        <TabsList className="bg-zinc-100 rounded-sm h-9">
          <TabsTrigger value="overview" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-overview">General</TabsTrigger>
          <TabsTrigger value="agents" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-agents">Agentes</TabsTrigger>
          <TabsTrigger value="tokens" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-tokens">Tokens / Costos</TabsTrigger>
          <TabsTrigger value="activity" className="text-xs rounded-sm data-[state=active]:bg-white" data-testid="tab-activity">Actividad</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Timeline */}
            <ChartCard title="Tickets - Ultimos 30 dias" className="lg:col-span-2" data-testid="chart-timeline">
              {loading ? <Skeleton className="h-[220px]" /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={timeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="ticketGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.2} />
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="count" name="Tickets" stroke={COLORS.primary} strokeWidth={2} fill="url(#ticketGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* By Priority */}
            <ChartCard title="Por Prioridad" data-testid="chart-priority">
              {loading ? <Skeleton className="h-[220px]" /> : byPriority.length === 0 ? (
                <div className="h-[220px] flex items-center justify-center text-xs text-zinc-400">Sin datos</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={byPriority}
                      dataKey="count"
                      nameKey="priority"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      innerRadius={40}
                      labelLine={false}
                      label={PieLabel}
                    >
                      {byPriority.map((entry, idx) => (
                        <Cell key={idx} fill={PRIORITY_COLORS[entry.priority] || PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span className="text-zinc-600 text-[11px] capitalize">{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {/* By Status */}
            <ChartCard title="Por Estado" data-testid="chart-status">
              {loading ? <Skeleton className="h-[200px]" /> : byStatus.length === 0 ? (
                <div className="h-[200px] flex items-center justify-center text-xs text-zinc-400">Sin datos</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={byStatus} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="status" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Tickets" radius={[3, 3, 0, 0]}>
                      {byStatus.map((entry, idx) => (
                        <Cell key={idx} fill={STATUS_COLORS[entry.status] || PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* By Channel */}
            <ChartCard title="Conversaciones por Canal" data-testid="chart-channel">
              {loading ? <Skeleton className="h-[200px]" /> : byChannel.length === 0 ? (
                <div className="h-[200px] flex items-center justify-center text-xs text-zinc-400">Sin datos</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={byChannel}
                      dataKey="count"
                      nameKey="channel"
                      cx="50%"
                      cy="50%"
                      outerRadius={70}
                      innerRadius={35}
                      labelLine={false}
                      label={PieLabel}
                    >
                      {byChannel.map((entry, idx) => (
                        <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span className="text-zinc-600 text-[11px] capitalize">{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Weekly Summary */}
            <ChartCard title="Resumen Semanal" data-testid="chart-weekly">
              {loading ? <Skeleton className="h-[200px]" /> : (
                <div className="h-[200px] flex flex-col justify-center space-y-4 px-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Tickets esta semana</span>
                    <span className="text-lg font-bold font-heading text-[#09090B]">{s.tickets.this_week ?? 0}</span>
                  </div>
                  <div className="h-px bg-brand-border" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Tickets hoy</span>
                    <span className="text-lg font-bold font-heading text-[#09090B]">{s.tickets.today ?? 0}</span>
                  </div>
                  <div className="h-px bg-brand-border" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">Conversaciones totales</span>
                    <span className="text-lg font-bold font-heading text-[#09090B]">{s.conversations.total ?? 0}</span>
                  </div>
                  <div className="h-px bg-brand-border" />
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-zinc-600">HITL activas</span>
                    <span className="text-lg font-bold font-heading text-brand-primary">{s.conversations.hitl_active ?? 0}</span>
                  </div>
                </div>
              )}
            </ChartCard>
          </div>
        </TabsContent>

        {/* Agents Tab */}
        <TabsContent value="agents" className="mt-4">
          <ChartCard title="Performance de Agentes" data-testid="agents-performance-table">
            {loading ? <Skeleton className="h-[300px]" /> : agents.length === 0 ? (
              <div className="h-[200px] flex items-center justify-center text-xs text-zinc-400">Sin agentes configurados</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="agents-table">
                  <thead>
                    <tr className="border-b border-brand-border">
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Agente</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Tipo</th>
                      <th className="text-center py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Conversaciones</th>
                      <th className="text-center py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Mensajes</th>
                      <th className="text-center py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Herramientas</th>
                      <th className="text-center py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Habilidades</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((a) => (
                      <tr key={a.agent_id} className="border-b border-brand-border/50 hover:bg-zinc-50 transition-colors" data-testid={`agent-perf-${a.agent_id}`}>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-sm bg-brand-primary/10 flex items-center justify-center">
                              <Bot className="w-3.5 h-3.5 text-brand-primary" />
                            </div>
                            <span className="font-medium text-[#09090B]">{a.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <Badge variant="outline" className="text-[10px] rounded-sm capitalize">{a.type}</Badge>
                        </td>
                        <td className="py-3 px-3 text-center font-mono font-semibold text-[#09090B]">{a.conversations_handled}</td>
                        <td className="py-3 px-3 text-center font-mono text-zinc-600">{a.messages_sent}</td>
                        <td className="py-3 px-3 text-center font-mono text-zinc-600">{a.tool_executions}</td>
                        <td className="py-3 px-3 text-center">
                          <Badge className="text-[10px] rounded-sm bg-brand-primary/10 text-brand-primary border-0">{a.skills_enabled}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>

          {/* Agent Load Bar Chart */}
          {!loading && agents.length > 0 && (
            <ChartCard title="Carga por Agente (Conversaciones)" className="mt-4" data-testid="chart-agent-load">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={agents} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: '#52525B' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="conversations_handled" name="Conversaciones" fill={COLORS.primary} radius={[0, 4, 4, 0]} barSize={20} />
                  <Bar dataKey="messages_sent" name="Mensajes" fill={COLORS.primaryLight} radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </TabsContent>

        {/* Tokens Tab */}
        <TabsContent value="tokens" className="mt-4">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div className="max-w-2xl space-y-1">
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                Consumo LLM del tenant. Click en un <code className="bg-zinc-100 px-0.5">conversation_id</code> abre
                Logs → Autopsia (incl. <code className="bg-zinc-100 px-0.5">ticket-chat-*</code>).
                {isAdmin ? ' Solo admin puede vaciar el historial de token_usage (no borra conversaciones ni autopsia).' : ''}
              </p>
              <p className="text-[11px] text-amber-800/90 bg-amber-50 border border-amber-100 rounded-sm px-2 py-1.5 leading-relaxed" data-testid="tokens-cost-tip">
                Los KPIs de tokens muestran <strong>cantidad</strong> (es-AR: punto = miles). El <strong>gasto</strong> es USD estimado (input + cached + output + reasoning) — no es la factura del proveedor.
                Un mensaje suele generar varias llamadas LLM; mirá «Por proposito».
              </p>
            </div>
            {isAdmin && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs rounded-sm border-red-200 text-red-700 hover:bg-red-50"
                data-testid="tokens-purge-open"
                onClick={() => { setPurgePhrase(''); setPurgeOpen(true); }}
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar consumos
              </Button>
            )}
          </div>
          {/* Token KPIs: rolling windows (not redundant total clones) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-2" data-testid="token-kpis">
            <KpiCard
              label="Ultimas 24h"
              value={formatTokens(w24?.tokens)}
              icon={Coins}
              color="text-brand-success"
              sub={windowSub(w24)}
              loading={loading}
            />
            <KpiCard
              label="Ultimos 7d"
              value={formatTokens(w7?.tokens)}
              icon={Coins}
              color="text-brand-warning"
              sub={windowSub(w7)}
              loading={loading}
            />
            <KpiCard
              label="Ultimos 30d"
              value={formatTokens(w30?.tokens)}
              icon={Coins}
              color="text-brand-primary"
              sub={windowSub(w30)}
              loading={loading}
            />
            <KpiCard
              label="Gasto est. 30d"
              value={w30?.cost_usd != null || w30?.cost != null ? formatUsd(w30.cost_usd ?? w30.cost) : '-'}
              icon={DollarSign}
              color="text-brand-danger"
              sub={w30?.usd_per_1m_tokens != null ? `tasa: ${Number(w30.usd_per_1m_tokens).toFixed(2)} USD por 1M tokens` : 'USD estimado'}
              loading={loading}
            />
          </div>
          {!loading && allTime && (
            <p className="text-[11px] text-zinc-400 mb-4" data-testid="tokens-all-time-secondary">
              Historico (all-time): {formatTokens(allTime.tokens ?? allTime.total_tokens)} · {formatUsd(allTime.cost_usd ?? allTime.total_cost)} · {allTime.calls ?? allTime.total_calls ?? 0} llamadas
              {(allTime.input_tokens != null || allTime.total_input != null) && (
                <>
                  {' '}· in {(allTime.input_tokens ?? allTime.total_input)?.toLocaleString('es-AR')}
                  {(allTime.cached_input_tokens > 0) ? ` (cached ${Number(allTime.cached_input_tokens).toLocaleString('es-AR')})` : ''}
                  {' / '}out {(allTime.output_tokens ?? allTime.total_output)?.toLocaleString('es-AR')}
                  {(allTime.reasoning_tokens > 0) ? ` / reasoning ${Number(allTime.reasoning_tokens).toLocaleString('es-AR')}` : ''}
                </>
              )}
            </p>
          )}

          {isAdmin && (
            <ChartCard title="Tarifario estimado (USD / 1M tokens)" className="mb-4" data-testid="tokens-pricing-editor">
              <p className="text-[11px] text-zinc-500 mb-3">
                Precios por model id. Cascada: override tenant → LiteLLM → defaults de codigo → fallback. Solo afecta llamadas nuevas. Cached = tarifa de prompt en cache (p. ej. Grok $0.30/1M).
              </p>
              {loading ? <Skeleton className="h-[120px]" /> : pricingRows.length === 0 ? (
                <div className="text-xs text-zinc-400 py-4">Sin modelos relevantes aun. Tras un uso LLM aparecen aqui.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-brand-border">
                        <th className="text-left py-2 px-2 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Modelo</th>
                        <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Input</th>
                        <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Cached</th>
                        <th className="text-right py-2 px-2 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Output</th>
                        <th className="text-left py-2 px-2 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Fuente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pricingRows.map((row) => (
                        <tr key={row.model} className="border-b border-brand-border/50">
                          <td className="py-2 px-2 font-mono text-xs">{row.model}</td>
                          <td className="py-2 px-2">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              className="h-7 text-xs rounded-sm font-mono text-right ml-auto w-24"
                              data-testid={`pricing-input-${row.model}`}
                              value={pricingDraft[row.model]?.input_usd_per_1m ?? ''}
                              onChange={(e) => setPricingDraft((prev) => ({
                                ...prev,
                                [row.model]: {
                                  ...(prev[row.model] || {}),
                                  input_usd_per_1m: e.target.value,
                                  cached_usd_per_1m: prev[row.model]?.cached_usd_per_1m ?? '',
                                  output_usd_per_1m: prev[row.model]?.output_usd_per_1m ?? '',
                                },
                              }))}
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              className="h-7 text-xs rounded-sm font-mono text-right ml-auto w-24"
                              data-testid={`pricing-cached-${row.model}`}
                              value={pricingDraft[row.model]?.cached_usd_per_1m ?? ''}
                              onChange={(e) => setPricingDraft((prev) => ({
                                ...prev,
                                [row.model]: {
                                  ...(prev[row.model] || {}),
                                  input_usd_per_1m: prev[row.model]?.input_usd_per_1m ?? '',
                                  cached_usd_per_1m: e.target.value,
                                  output_usd_per_1m: prev[row.model]?.output_usd_per_1m ?? '',
                                },
                              }))}
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              className="h-7 text-xs rounded-sm font-mono text-right ml-auto w-24"
                              data-testid={`pricing-output-${row.model}`}
                              value={pricingDraft[row.model]?.output_usd_per_1m ?? ''}
                              onChange={(e) => setPricingDraft((prev) => ({
                                ...prev,
                                [row.model]: {
                                  ...(prev[row.model] || {}),
                                  input_usd_per_1m: prev[row.model]?.input_usd_per_1m ?? '',
                                  cached_usd_per_1m: prev[row.model]?.cached_usd_per_1m ?? '',
                                  output_usd_per_1m: e.target.value,
                                },
                              }))}
                            />
                          </td>
                          <td className="py-2 px-2">
                            <Badge variant="outline" className="text-[9px] rounded-sm capitalize">{row.source}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="mt-3 flex justify-end">
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 text-xs rounded-sm"
                      data-testid="tokens-pricing-save"
                      disabled={pricingSaving}
                      onClick={savePricing}
                    >
                      {pricingSaving ? 'Guardando…' : 'Guardar tarifario'}
                    </Button>
                  </div>
                </div>
              )}
            </ChartCard>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {/* Token Timeline */}
            <ChartCard title="Consumo diario - Ultimos 30 dias" className="lg:col-span-2" data-testid="chart-tokens-timeline">
              {loading ? <Skeleton className="h-[220px]" /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={tokensTimeline} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="tokenGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#7C3AED" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#7C3AED" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="tokens" name="Tokens" stroke="#7C3AED" strokeWidth={2} fill="url(#tokenGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* By Purpose */}
            <ChartCard title="Por Proposito" data-testid="chart-tokens-purpose">
              {loading ? <Skeleton className="h-[220px]" /> : tokensByPurpose.length === 0 ? (
                <div className="h-[220px] flex items-center justify-center text-xs text-zinc-400">Sin datos de consumo aun</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={tokensByPurpose} dataKey="total_tokens" nameKey="purpose" cx="50%" cy="50%" outerRadius={80} innerRadius={40} labelLine={false} label={PieLabel}>
                      {tokensByPurpose.map((_, idx) => (
                        <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<TooltipDeConsumo />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => <span className="text-zinc-600 text-[11px] capitalize">{v}</span>} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>

          {/* By Model table */}
          <ChartCard title="Consumo por Modelo" data-testid="tokens-by-model-table">
            {loading ? <Skeleton className="h-[200px]" /> : tokensByModel.length === 0 ? (
              <div className="h-[150px] flex items-center justify-center text-xs text-zinc-400">Sin datos de consumo aun. Los tokens se registran con cada interaccion del agente.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="model-usage-table">
                  <thead>
                    <tr className="border-b border-brand-border">
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Modelo</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Provider</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Input</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500" title="Parte del input que el proveedor sirvio de su cache. Se paga a una fraccion.">En cache</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Output</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500" title="Tokens de razonamiento. Se pagan como salida, que es la tarifa cara.">Razonam.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Total</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Costo USD est.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Llamadas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokensByModel.map((m, idx) => (
                      <tr key={idx} className="border-b border-brand-border/50 hover:bg-zinc-50 transition-colors">
                        <td className="py-3 px-3 font-mono font-medium text-[#09090B] text-xs">{m.model}</td>
                        <td className="py-3 px-3"><Badge variant="outline" className="text-[10px] rounded-sm capitalize">{m.provider}</Badge></td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-zinc-600">{miles(m.input_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-brand-success">{miles(m.cached_input_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-zinc-600">{miles(m.output_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-brand-danger">{miles(m.reasoning_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs font-semibold text-[#09090B]">{miles(m.total_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs font-semibold text-brand-danger">{formatUsd(m.cost_usd)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-zinc-600">{m.calls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>

          {/* Por agente: la unica tabla que contesta «a cual le sobra el modelo que razona».
              Va al lado de la de modelos porque la decision se toma cruzando las dos: el agente
              que paga razonamiento, y con que modelo esta corriendo. */}
          <ChartCard title="Consumo por Agente" className="mt-4" data-testid="tokens-by-agent-table">
            <p className="text-[11px] text-zinc-500 mb-3">
              El <b>razonamiento se paga como salida</b>, que es la tarifa cara. Un agente que hace
              trabajo mecanico —clasificar, rerankear, extraer— y paga mucho razonamiento es
              candidato a un modelo que no razone.
            </p>
            {loading ? <Skeleton className="h-[160px]" /> : tokensByAgent.length === 0 ? (
              <div className="h-[120px] flex items-center justify-center text-xs text-zinc-400">Sin datos de consumo aun. Los tokens se registran con cada interaccion del agente.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="agent-usage-table">
                  <thead>
                    <tr className="border-b border-brand-border">
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Agente</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Modelos</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Input</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500" title="Parte del input que el proveedor sirvio de su cache. Se paga a una fraccion.">En cache</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Output</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500" title="Tokens de razonamiento. Se pagan como salida, que es la tarifa cara.">Razonam.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Total</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Costo USD est.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Llamadas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokensByAgent.map((a, idx) => (
                      <tr key={a.agent_id ?? `sin-agente-${idx}`} className="border-b border-brand-border/50 hover:bg-zinc-50 transition-colors" data-testid={`agent-usage-row-${a.agent_id || 'sin-agente'}`}>
                        <td className="py-3 px-3 text-xs font-medium text-[#09090B]">
                          {/* Sin agente —un rerank, una clasificacion del coordinador— va un guion
                              y no un nombre inventado: el gasto es real y la atribucion no se sabe. */}
                          {a.name || <span className="text-zinc-400" title="Llamadas sin agente: clasificacion, rerank, tareas del motor">— sin agente —</span>}
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex gap-1 flex-wrap">
                            {(a.models_used || []).map((m, i) => (
                              <Badge key={i} variant="outline" className="text-[9px] rounded-sm font-mono">{m}</Badge>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-zinc-600">{miles(a.input_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-brand-success">{miles(a.cached_input_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-zinc-600">{miles(a.output_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-brand-danger">{miles(a.reasoning_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs font-semibold text-[#09090B]">{miles(a.total_tokens)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs font-semibold text-brand-danger">{formatUsd(a.cost_usd)}</td>
                        <td className="py-3 px-3 text-right font-mono text-xs text-zinc-600">{a.calls}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>

          {/* Top conversations by token usage */}
          {tokensByConvo.length > 0 && (
            <ChartCard title="Top Conversaciones por Consumo" className="mt-4" data-testid="tokens-by-conversation">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-border">
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Conversacion</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Tokens</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500" title="Parte del input que el proveedor sirvio de su cache. Se paga a una fraccion.">En cache</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500" title="Tokens de razonamiento. Se pagan como salida, que es la tarifa cara.">Razonam.</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Costo USD</th>
                      <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Llamadas</th>
                      <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-[0.08em] text-zinc-500">Modelos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tokensByConvo.map((c, idx) => {
                      const cid = c.conversation_id || '';
                      const ticketId = ticketIdFromConversationId(cid);
                      const short = cid.length > 18 ? `${cid.substring(0, 16)}…` : cid;
                      return (
                        <tr key={idx} className="border-b border-brand-border/50 hover:bg-zinc-50 transition-colors">
                          <td className="py-3 px-3">
                            <div className="flex flex-col gap-0.5">
                              <Link
                                to={conversationAutopsyHref(cid)}
                                title={cid}
                                className="font-mono text-xs text-brand-primary hover:underline inline-flex items-center gap-1"
                                data-testid={`tokens-convo-link-${idx}`}
                              >
                                {short} <ExternalLink className="w-3 h-3 shrink-0 opacity-60" />
                              </Link>
                              {ticketId && (
                                <Link
                                  to={`/tickets?ticket=${encodeURIComponent(ticketId)}`}
                                  className="text-[10px] text-zinc-500 hover:text-brand-primary hover:underline"
                                >
                                  Ticket {ticketId}
                                </Link>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-right font-mono text-xs font-semibold text-[#09090B]">{miles(c.total_tokens)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs text-brand-success">{miles(c.cached_input_tokens)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs text-brand-danger">{miles(c.reasoning_tokens)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs text-brand-danger">${c.cost_usd?.toFixed(4)}</td>
                          <td className="py-3 px-3 text-right font-mono text-xs text-zinc-600">{c.calls}</td>
                          <td className="py-3 px-3">
                            <div className="flex gap-1 flex-wrap">
                              {c.models_used?.map((m, i) => (
                                <Badge key={i} variant="outline" className="text-[9px] rounded-sm">{m}</Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </ChartCard>
          )}

          <ConfirmActionDialog
            open={purgeOpen}
            onOpenChange={(open) => {
              setPurgeOpen(open);
              if (!open) setPurgePhrase('');
            }}
            title="Eliminar historial de consumos LLM"
            description="Borra todos los registros de token_usage de este tenant. No elimina conversaciones, tickets ni autopsia. Es irreversible."
            confirmText="Eliminar consumos"
            confirmDisabled={purgePhrase.trim() !== TOKEN_PURGE_PHRASE}
            onConfirm={async () => {
              try {
                const res = await analyticsAPI.tokensPurge(TOKEN_PURGE_PHRASE);
                toast.success(`Eliminados ${res.data?.deleted_count ?? 0} registros de consumo`);
                await loadData();
              } catch (err) {
                toast.error(formatApiError(err));
                throw err;
              }
            }}
          >
            <p className="text-xs text-zinc-600 mb-2">
              Escribí <code className="bg-zinc-100 px-1 rounded-sm">{TOKEN_PURGE_PHRASE}</code> para habilitar la confirmacion.
            </p>
            <Input
              data-testid="tokens-purge-phrase"
              className="h-8 text-xs rounded-sm font-mono"
              value={purgePhrase}
              onChange={(e) => setPurgePhrase(e.target.value)}
              placeholder={TOKEN_PURGE_PHRASE}
              autoComplete="off"
            />
          </ConfirmActionDialog>
        </TabsContent>

        {/* Activity Tab */}
        <TabsContent value="activity" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Recent Tickets */}
            <ChartCard title="Tickets Recientes" data-testid="recent-tickets-list">
              {loading ? <Skeleton className="h-[300px]" /> : activity.recent_tickets.length === 0 ? (
                <div className="h-[200px] flex items-center justify-center text-xs text-zinc-400">Sin tickets recientes</div>
              ) : (
                <div className="divide-y divide-brand-border/50 -mx-5">
                  {activity.recent_tickets.map((t, idx) => (
                    <div key={t.ticket_id || idx} className="px-5 py-3 flex items-center justify-between hover:bg-zinc-50 transition-colors" data-testid={`recent-ticket-${t.ticket_id}`}>
                      <div className="min-w-0 flex-1 mr-3">
                        <p className="text-sm font-medium text-[#09090B] truncate">{t.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] font-mono text-zinc-400">{t.ticket_id}</span>
                          {t.created_at && <span className="text-[10px] text-zinc-400">{new Date(t.created_at).toLocaleDateString('es-AR')}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Badge
                          className="text-[10px] rounded-sm border-0 text-white"
                          style={{ backgroundColor: PRIORITY_COLORS[t.priority] || '#A1A1AA' }}
                        >
                          {t.priority}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] rounded-sm capitalize">{t.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ChartCard>

            {/* Recent Events */}
            <ChartCard title="Eventos del Sistema" data-testid="recent-events-list">
              {loading ? <Skeleton className="h-[300px]" /> : activity.recent_events.length === 0 ? (
                <div className="h-[200px] flex flex-col items-center justify-center text-zinc-400">
                  <Activity className="w-8 h-8 mb-2 opacity-30" />
                  <p className="text-xs">Sin eventos recientes</p>
                  <p className="text-[10px] mt-1">Los eventos de HITL y heartbeat apareceran aqui</p>
                </div>
              ) : (
                <div className="divide-y divide-brand-border/50 -mx-5">
                  {activity.recent_events.map((e, idx) => (
                    <div key={idx} className="px-5 py-3 hover:bg-zinc-50 transition-colors" data-testid={`event-${idx}`}>
                      <div className="flex items-center gap-2">
                        <Zap className="w-3.5 h-3.5 text-brand-warning flex-shrink-0" />
                        <span className="text-xs font-semibold text-zinc-700 uppercase">{e.event_type?.replace(/_/g, ' ')}</span>
                        {e.timestamp && <span className="text-[10px] text-zinc-400 ml-auto">{fechaYHora(e.timestamp)}</span>}
                      </div>
                      {e.description && <p className="text-xs text-zinc-500 mt-1 ml-5">{e.description}</p>}
                      {e.conversation_id && <p className="text-[10px] font-mono text-zinc-400 mt-0.5 ml-5">{e.conversation_id}</p>}
                    </div>
                  ))}
                </div>
              )}
            </ChartCard>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
